import { useSyncExternalStore } from 'react';

import { AnyFunction } from 'types';
import { capitalize } from 'utils/text';

/**
 * What is this? Why not use react context??
 *
 * This is a very simple and optimized solution to one of react's context
 * biggest flaws: every time something inside our context.value changes,
 * EVERY SINGLE COMPONENT THAT IS USING OUR CONTEXT RE-RENDERS
 *
 * This obviously should be the expected behavior in cases we have a tight context data
 * that is closely related
 *
 * But in some cases we do need specificity.
 *
 * -------------------------------------------------------
 *
 * Take the 'Online' provider here as an example:
 *
 * - Its a simple state, that tracks the ids of all the online users right now
 * - Every time we receive an update to that list via real time integration we update it
 * - The use case as of time of writing is one: show the little online indicator on each UserAvatar
 * ---> This means that, if we control this very simple state with state+context,
 * ---> EVER.SINGLE UserAvatar will re-render EVERY TIME any user change happens on our list
 *
 * Yes, its a small thing at scale, but its the best representation of the issue
 *
 * Since each avatar only cares if the online result is true/false, its MUCH BETTER
 * if we simply have a selective hook, tied to the global state (the id list) that
 * receives the id we want to check and returns just that.
 *
 * That way, we only every render again if the boolean flips
 *
 * ---------------------------------------------------------
 *
 * This is a simple yet effective example of why this is a needed thing missing in react's context
 * implementation. This should not be the rule, obviously. You can see we have a lot of contexts in
 * use. But when we have a more broad state and need selective checks, this is the way to go.
 *
 *
 * NOTE: Remember there's still the DataContext+SetterContext in which we split the context state in
 * its data side (eg a project list) and its updater/actions side (eg an updateProject action) as those actions
 * tend to be unique and not reliant on the state itself, meaning components that only care about firing actions
 * to the context can still use that hook/context without re-rendering on data changes...
 *
 * Whichever strategy to adopt should change based on the requirements of the functionality/state
 */

interface BaseStore {
  subscribe: (cb: AnyFunction) => AnyFunction;
}

interface StoreConfig {
  actions: Record<string, AnyFunction>;
  selectors: Record<string, AnyFunction>;

  /**
   * FALSE by default
   */
  exposeStore?: boolean;
}

type WithStore<T extends StoreConfig> = T['exposeStore'] extends true
  ? { store: BaseStore }
  : object;

type StoreSelectorsMap<T extends StoreConfig> = {
  [A in keyof T['selectors']]: {
    hookName: `use${Capitalize<Extract<A, string>>}`;
    name: A;
    selector: T['selectors'][A];
  };
}[keyof T['selectors']];

type StoreSelectorHooks<T extends StoreConfig> = {
  [Hook in StoreSelectorsMap<T>['hookName']]: Extract<
    StoreSelectorsMap<T>,
    { hookName: Hook }
  >['selector'];
};

function emitStoreChange(listeners: Set<AnyFunction>) {
  listeners.forEach((cb) => cb());
}

function createStoreSelectionHook<Selector extends AnyFunction>(
  store: BaseStore,
  selector: AnyFunction,
) {
  return (...params: Parameters<Selector>) =>
    useSyncExternalStore(store.subscribe, () => selector(...params));
}

export function createExternalStore<T extends StoreConfig>({
  actions,
  selectors,
  exposeStore = false,
}: T) {
  const listeners = new Set<AnyFunction>();

  const store = {
    subscribe(listener: AnyFunction) {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };

  const withStore = (exposeStore ? { store } : {}) as WithStore<T>;

  return {
    ...withStore,

    ...(Object.fromEntries(
      Object.entries(selectors).map(([name, selector]) => [
        `use${capitalize(name)}`,
        createStoreSelectionHook(store, selector),
      ]),
    ) as StoreSelectorHooks<T>),

    ...(Object.fromEntries(
      Object.entries(actions).map(([name, action]) => [
        name,
        (...params) => {
          action(...params);
          emitStoreChange(listeners);
        },
      ]),
    ) as T['actions']),
  };
}
